/**
 * WitnessValidatorPanel — interactive PreservationWitness validator.
 *
 * Lets the reader build a PreservationWitness against an
 * IssuerCapability, flip one field at a time, and watch the verdict
 * change. The validator is the same pure function the test suite
 * exercises, so every verdict in the UI is the verdict the rulebook
 * would issue.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo, useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { severityColor } from "../severityColor.js";
import {
  FieldGroup,
  FieldToggle,
} from "./witness-validator-panel/FieldControls.js";
import { VerdictPanel } from "./witness-validator-panel/VerdictPanel.js";
import {
  validatePreservationWitness,
  type AbsDiffClass,
  type DriftSeverity,
  type IssuerCapabilityInput,
  type Kind,
  type PreservationWitnessInput,
} from "../algorithms/validatePreservation.js";

const ACCENT = "#22c55e";

const KINDS: readonly Kind[] = ["Algebraic", "ResourceBound"];
const ABS_CLASSES: readonly AbsDiffClass[] = [
  "equivalent",
  "tightened",
  "expanded",
  "unknown",
];
const DRIFT_SEVERITIES: readonly DriftSeverity[] = [
  "low",
  "medium",
  "high",
  "unknown",
];
type MaxDriftSeverity = DriftSeverity | "none";

interface ChipItem<Id extends string = string> {
  readonly id: Id;
  readonly label: string;
  readonly color: string;
}

function chipItems<Id extends string>(
  options: readonly Id[],
  color = ACCENT,
): readonly ChipItem<Id>[] {
  return options.map((option) => ({
    id: option,
    label: option,
    color,
  }));
}

const INVARIANT_CHIPS = chipItems([
  "INV-A-NOAMBIENT",
  "INV-R-BUDGET",
  "INV-OTHER",
] as const);
const KIND_CHIPS = chipItems(KINDS);
const ABS_CLASS_CHIPS = chipItems(ABS_CLASSES, panelTokens.accentBlue);
const DRIFT_SEVERITY_CHIPS = chipItems(DRIFT_SEVERITIES, panelTokens.accentAmber);
const ISSUER_CHIPS = chipItems(["ISSUER-ALPHA", "ISSUER-BETA"] as const);
const MAX_DRIFT_SEVERITY_CHIPS = chipItems<MaxDriftSeverity>(
  ["none", ...DRIFT_SEVERITIES],
  panelTokens.accentAmber,
);

const DEFAULT_ICAP: IssuerCapabilityInput = {
  issuer_id: "ISSUER-ALPHA",
  may_sign_artifact_types: [
    "EstablishReceipt",
    "PreservationWitness",
    "DriftReceipt",
    "MigrationArtifact",
  ],
  invariant_ids: ["INV-A-NOAMBIENT", "INV-R-BUDGET"],
  kinds: ["Algebraic", "ResourceBound"],
  change_classes: ["code", "config", "deps", "accounting"],
};

const DEFAULT_WITNESS: PreservationWitnessInput = {
  invariant_id: "INV-A-NOAMBIENT",
  kind: "Algebraic",
  abs_diff_class: "equivalent",
  drift_severity: "low",
  drift_receipt_present: true,
  issuer_id: "ISSUER-ALPHA",
  fingerprint_continuous: true,
  evidence_present: true,
};

interface PresetCase {
  readonly id: string;
  readonly label: string;
  readonly note: string;
  readonly witness: PreservationWitnessInput;
  readonly icap?: Partial<IssuerCapabilityInput>;
}

const PRESETS: readonly PresetCase[] = [
  {
    id: "happy-path",
    label: "Happy path (A-type, equivalent ABS)",
    note: "tv-16 — accepts.",
    witness: DEFAULT_WITNESS,
  },
  {
    id: "abs-unknown",
    label: "ABS unknown",
    note: "tv-17 — ILG-P-004 fires.",
    witness: {
      ...DEFAULT_WITNESS,
      abs_diff_class: "unknown",
      evidence_present: false,
    },
  },
  {
    id: "r-accounting-drift",
    label: "R-type accounting drift, no MIG",
    note: "tv-18 — ILG-P-009 fires.",
    witness: {
      ...DEFAULT_WITNESS,
      invariant_id: "INV-R-BUDGET",
      kind: "ResourceBound",
      accounting_drift: true,
      mig_present: false,
    },
  },
  {
    id: "out-of-scope-issuer",
    label: "Issuer out of scope (Sᵁ)",
    note: "ILG-IC-002 fires.",
    witness: { ...DEFAULT_WITNESS, invariant_id: "INV-OTHER" },
  },
  {
    id: "fingerprint-break",
    label: "Fingerprint discontinuity (A3)",
    note: "ILG-P-002 fires.",
    witness: { ...DEFAULT_WITNESS, fingerprint_continuous: false },
  },
  {
    id: "drift-high",
    label: "Drift severity high (Sᴹ)",
    note: "ILG-P-005 fires.",
    witness: { ...DEFAULT_WITNESS, drift_severity: "high" },
  },
];

export function WitnessValidatorPanel(): React.JSX.Element {
  const [witness, setWitness] =
    useState<PreservationWitnessInput>(DEFAULT_WITNESS);
  const [icap, setIcap] = useState<IssuerCapabilityInput>(DEFAULT_ICAP);

  const verdict = useMemo(
    () => validatePreservationWitness(witness, icap),
    [witness, icap],
  );
  const witnessJson = useMemo(
    () => JSON.stringify(witness, null, 2),
    [witness],
  );
  const scopeSnapshot = useMemo(
    () => `issuer_id: ${icap.issuer_id}
may_sign:  ${icap.may_sign_artifact_types.join(", ")}
invariant_ids: ${icap.invariant_ids.join(", ")}
kinds:     ${icap.kinds.join(", ")}
change_classes: ${icap.change_classes.join(", ")}
revoked: ${String(icap.revoked === true)}
expired: ${String(icap.expired === true)}
forbid_expanded_abs: ${String(icap.forbid_expanded_or_unknown_abs === true)}
max_drift_severity: ${icap.max_drift_severity ?? "(none)"}`,
    [icap],
  );

  const updateWitness = (patch: Partial<PreservationWitnessInput>): void => {
    setWitness((prev) => ({ ...prev, ...patch }));
  };

  const updateIcap = (patch: Partial<IssuerCapabilityInput>): void => {
    setIcap((prev) => ({ ...prev, ...patch }));
  };

  const applyPreset = (preset: PresetCase): void => {
    setWitness(preset.witness);
    setIcap({ ...DEFAULT_ICAP, ...preset.icap });
  };

  return (
    <PanelSurface.PanelShell
      title="Witness Validator"
      subtitle="Build a PreservationWitness — watch the verdict change as you flip one field"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Quick presets</PanelSurface.SectionHeading>
        <div
          className="mt-3.5 grid gap-2"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {PRESETS.map((preset) => (
            <PanelSurface.SelectableCard
              key={preset.id}
              active={witness === preset.witness}
              color={ACCENT}
              onClick={() => {
                applyPreset(preset);
              }}
            >
              <span className="block text-[13px] font-semibold text-[var(--ilg-bright)]">
                {preset.label}
              </span>
              <span className="block mt-1 text-[11px] text-[var(--ilg-muted)] font-mono">
                {preset.note}
              </span>
            </PanelSurface.SelectableCard>
          ))}
        </div>
      </PanelSurface.Card>

      <div
        className="grid gap-3.5 items-start"
        style={{
          gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
        }}
      >
        <PanelSurface.Card>
          <PanelSurface.SectionHeading>PreservationWitness fields</PanelSurface.SectionHeading>
          <div className="mt-3.5 flex flex-col gap-2.5">
            <FieldGroup label="invariant_id">
              <PanelSurface.ChipRow
                activeId={witness.invariant_id}
                ariaLabel="Invariant identifier"
                items={INVARIANT_CHIPS}
                onChange={(invariant_id) => {
                  updateWitness({ invariant_id });
                }}
              />
            </FieldGroup>

            <FieldGroup label="kind">
              <PanelSurface.ChipRow
                activeId={witness.kind}
                ariaLabel="Witness kind"
                items={KIND_CHIPS}
                onChange={(kind) => {
                  updateWitness({ kind });
                }}
              />
            </FieldGroup>

            <FieldGroup label="abs_diff_class (admission boundary diff)">
              <PanelSurface.ChipRow
                activeId={witness.abs_diff_class}
                ariaLabel="Admission boundary diff class"
                items={ABS_CLASS_CHIPS}
                onChange={(abs_diff_class) => {
                  updateWitness({ abs_diff_class });
                }}
              />
            </FieldGroup>

            <FieldGroup label="drift_severity (DriftReport)">
              <PanelSurface.ChipRow
                activeId={witness.drift_severity}
                ariaLabel="Drift severity"
                items={DRIFT_SEVERITY_CHIPS}
                onChange={(drift_severity) => {
                  updateWitness({ drift_severity });
                }}
              />
            </FieldGroup>

            <FieldGroup label="issuer_id (who signed the witness)">
              <PanelSurface.ChipRow
                activeId={witness.issuer_id}
                ariaLabel="Witness issuer"
                items={ISSUER_CHIPS}
                onChange={(issuer_id) => {
                  updateWitness({ issuer_id });
                }}
              />
            </FieldGroup>

            <FieldToggle
              label="drift_receipt_present"
              value={witness.drift_receipt_present}
              onChange={(v) => {
                updateWitness({ drift_receipt_present: v });
              }}
              helpWhenFalse="ILG-P-001 will fire — preservation refused."
            />

            <FieldToggle
              label="fingerprint_continuous"
              value={witness.fingerprint_continuous}
              onChange={(v) => {
                updateWitness({ fingerprint_continuous: v });
              }}
              helpWhenFalse="ILG-P-002 will fire — preservation cannot Re-establish."
            />

            <FieldToggle
              label="evidence_present (preservation_evidence non-empty)"
              value={witness.evidence_present}
              onChange={(v) => {
                updateWitness({ evidence_present: v });
              }}
            />

            {witness.kind === "ResourceBound" && (
              <>
                <FieldToggle
                  label="accounting_drift"
                  value={witness.accounting_drift === true}
                  onChange={(v) => {
                    updateWitness({ accounting_drift: v });
                  }}
                />
                <FieldToggle
                  label="mig_present"
                  value={witness.mig_present === true}
                  onChange={(v) => {
                    updateWitness({ mig_present: v });
                  }}
                  helpWhenFalse="If accounting_drift = true, ILG-P-009 will fire."
                />
                <FieldToggle
                  label="budget_increase_without_release"
                  value={witness.budget_increase_without_release === true}
                  onChange={(v) => {
                    updateWitness({ budget_increase_without_release: v });
                  }}
                />
              </>
            )}
          </div>

          <PanelSurface.SectionHeading style={{ marginTop: 18 }}>
            IssuerCapability scope (editable)
          </PanelSurface.SectionHeading>
          <div className="mt-3 flex flex-col gap-2.5">
            <FieldToggle
              label="revoked (ICAP revocation list flag)"
              value={icap.revoked === true}
              onChange={(v) => {
                updateIcap({ revoked: v });
              }}
              helpWhenFalse=""
            />
            <FieldToggle
              label="expired (past ICAP.expires_at)"
              value={icap.expired === true}
              onChange={(v) => {
                updateIcap({ expired: v });
              }}
              helpWhenFalse=""
            />
            <FieldToggle
              label="forbid_expanded_or_unknown_abs (ICAP hard cap)"
              value={icap.forbid_expanded_or_unknown_abs === true}
              onChange={(v) => {
                updateIcap({ forbid_expanded_or_unknown_abs: v });
              }}
              helpWhenFalse=""
            />
            <FieldGroup label="max_drift_severity (ICAP hard cap)">
              <PanelSurface.ChipRow
                activeId={icap.max_drift_severity ?? "none"}
                ariaLabel="Maximum drift severity"
                items={MAX_DRIFT_SEVERITY_CHIPS}
                onChange={(v) => {
                  updateIcap({
                    max_drift_severity: v === "none" ? undefined : v,
                  });
                }}
              />
            </FieldGroup>
          </div>
          <PanelSurface.SectionHeading style={{ marginTop: 18 }}>
            Scope snapshot
          </PanelSurface.SectionHeading>
          <PanelSurface.Mono>{scopeSnapshot}</PanelSurface.Mono>
        </PanelSurface.Card>

        <div className="flex flex-col gap-3.5">
          <VerdictPanel verdict={verdict} />

          <PanelSurface.Card>
            <PanelSurface.SectionHeading>Witness JSON (canonical shape)</PanelSurface.SectionHeading>
            <PanelSurface.Mono>{witnessJson}</PanelSurface.Mono>
          </PanelSurface.Card>

          <PanelSurface.Card>
            <PanelSurface.SectionHeading>Coverage at a glance</PanelSurface.SectionHeading>
            <div
              className="mt-3 grid gap-2.5"
              style={{
                gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
              }}
            >
              <PanelSurface.KpiBox
                label="primary code"
                value={verdict.outcome === "accept" ? "—" : verdict.rule}
                color={
                  verdict.outcome === "accept"
                    ? panelTokens.accentGreen
                    : severityColor(verdict.severity)
                }
              />
              <PanelSurface.KpiBox
                label="verdict"
                value={verdict.outcome.toUpperCase()}
                color={
                  verdict.outcome === "accept" ? panelTokens.accentGreen : panelTokens.accentRed
                }
              />
              <PanelSurface.KpiBox
                label="kind"
                value={witness.kind === "Algebraic" ? "A" : "R"}
                color={panelTokens.accentBlue}
              />
            </div>
          </PanelSurface.Card>
        </div>
      </div>
    </PanelSurface.PanelShell>
  );
}
